import { ChevronDown } from 'lucide-react'
import { validations } from '../../utils/utils'
import { US_STATES, US_STATE_CODES } from '../../data/usStates'
import { fieldClass, fieldErrorClass, labelClass } from './RegistrationShell'

// The sign-up form's fields, rules and inputs, shared by the one-page sign-up's shop and account steps.

// Same rules the password-reset form shows, so a password that works there works here.
export const PASSWORD_RULES = [
  { key: 'length', label: 'At least 8 characters and less than 22', test: (p) => p.length >= 8 && p.length <= 21 },
  { key: 'lower', label: 'A lowercase letter', test: (p) => /[a-z]/.test(p) },
  { key: 'upper', label: 'An uppercase letter', test: (p) => /[A-Z]/.test(p) },
  { key: 'number', label: 'A number', test: (p) => /[0-9]/.test(p) },
  { key: 'special', label: 'A special character (!, #, $, ?)', test: (p) => p.length > 0 && !/^[A-Za-z0-9 ]+$/.test(p) }
]

export const EMPTY_SIGN_UP = {
  company_name: '',
  email: '',
  phone: '',
  shop_address: '',
  shop_city: '',
  shop_state: '',
  shop_zip: '',
  owner_name: '',
  owner_email: '',
  owner_phone: '',
  password: '',
  confirm: ''
}

// The database column sizes (calibration_center / calibration_center_registration /
// application_user). A value longer than its column can't be stored, so the form stops there.
export const FIELD_LIMITS = {
  company_name: 70,
  email: 128,
  phone: 10,
  shop_address: 55,
  shop_city: 20,
  shop_zip: 12,
  owner_name: 50,
  owner_email: 50,
  owner_phone: 10
}

// Same patterns as the backend (RegistrationFieldRules), so the two never disagree.
// A US number as exactly 10 digits (area code + number) — no +1, spaces or punctuation.
const US_PHONE = /^\d{10}$/
const PHONE_FIELDS = ['phone', 'owner_phone']
const US_ZIP = /^\d{5}(-\d{4})?$/
const CITY = /^[A-Za-z][A-Za-z .'-]*$/

const tooLong = (name, label) => `${label} can be up to ${FIELD_LIMITS[name]} characters, including spaces`

/** Digits only, without a leading US country code (1), at most 10. */
export const usPhoneDigits = (raw) => {
  let digits = String(raw ?? '').replace(/\D/g, '')
  if (digits.length > 10 && digits.startsWith('1')) digits = digits.slice(1) // +1 country code
  return digits.slice(0, 10)
}

/** "(303) 555-0142" for the digits typed so far — what the phone fields show. */
export const formatUsPhone = (digits) => {
  const d = usPhoneDigits(digits)
  if (d.length === 0) return ''
  if (d.length <= 3) return `(${d}`
  if (d.length <= 6) return `(${d.slice(0, 3)}) ${d.slice(3)}`
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`
}

/** The shown phone format — its length is the phone inputs' max length. */
export const US_PHONE_FORMAT = '(XXX) XXX-XXXX'

// Field-level validation. Country is not collected: self-serve sign-up is US-only and the
// backend fixes shop_country to US.
export const validateRegistration = (f) => {
  const e = {}
  const v = (name) => (f[name] || '').trim()

  if (!v('company_name')) e.company_name = 'Shop name is required'
  else if (v('company_name').length < 2) e.company_name = 'Shop name must be at least 2 characters'
  else if (v('company_name').length > FIELD_LIMITS.company_name) e.company_name = tooLong('company_name', 'Shop name')

  if (!validations.email(v('email'))) e.email = 'Enter a valid shop email'
  else if (v('email').length > FIELD_LIMITS.email) e.email = tooLong('email', 'Shop email')

  if (!v('phone')) e.phone = 'Shop phone is required'
  else if (!US_PHONE.test(v('phone'))) e.phone = 'Enter all 10 digits, e.g. (303) 555-0142'

  if (!v('shop_address')) e.shop_address = 'Address is required'
  else if (v('shop_address').length < 5) e.shop_address = 'Enter the full address'
  else if (v('shop_address').length > FIELD_LIMITS.shop_address) e.shop_address = tooLong('shop_address', 'Address')

  if (!v('shop_city')) e.shop_city = 'City is required'
  else if (v('shop_city').length > FIELD_LIMITS.shop_city) e.shop_city = `Up to ${FIELD_LIMITS.shop_city} characters`
  else if (!CITY.test(v('shop_city'))) e.shop_city = "Letters, spaces and . ' - only"

  if (!US_STATE_CODES.has(v('shop_state').toUpperCase())) e.shop_state = 'Select a state'

  if (!v('shop_zip')) e.shop_zip = 'ZIP is required'
  else if (!US_ZIP.test(v('shop_zip'))) e.shop_zip = '5 digits or ZIP+4'

  if (v('owner_name').length < 3 || v('owner_name').length > FIELD_LIMITS.owner_name) {
    e.owner_name = `Enter your full name (3–${FIELD_LIMITS.owner_name} characters)`
  }
  if (!validations.email(v('owner_email')) || v('owner_email').length > FIELD_LIMITS.owner_email) {
    e.owner_email = `Enter a valid email (${FIELD_LIMITS.owner_email} characters max)`
  }
  if (!v('owner_phone')) e.owner_phone = 'Mobile number is required'
  else if (!US_PHONE.test(v('owner_phone'))) e.owner_phone = 'Enter all 10 digits, e.g. (303) 555-0142'

  if (!PASSWORD_RULES.every((r) => r.test(f.password))) e.password = 'Password does not meet the requirements'
  if (f.confirm !== f.password) e.confirm = 'Passwords do not match'
  return e
}

// Live "12/55" counter: the limit is the database column, so show it before it bites.
export const CharCount = ({ value, max }) => {
  const used = value.length
  const tone = used >= max ? 'text-red-300' : used >= max - 5 ? 'text-amber-300' : 'text-white/40'
  return (
    <span className={`text-[10px] font-medium tabular-nums ${tone}`} aria-live='polite'>
      {used}/{max}
    </span>
  )
}

export const FieldError = ({ id, error }) =>
  error ? (
    <div id={id} className='mt-1 pl-1 text-[11px] text-red-300'>
      {error}
    </div>
  ) : null

export const Field = ({
  name,
  label,
  value,
  onChange,
  onBlur,
  error,
  type = 'text',
  placeholder,
  autoComplete,
  maxLength,
  required,
  showCount,
  inputMode,
  hint
}) => (
  <div>
    <div className='flex items-baseline justify-between'>
      <label htmlFor={`reg-${name}`} className={labelClass}>
        {label}
        {required && <span className='text-white/40'> *</span>}
      </label>
      {showCount && maxLength && <CharCount value={value} max={maxLength} />}
    </div>
    <input
      id={`reg-${name}`}
      name={name}
      type={type}
      value={value}
      onChange={onChange}
      onBlur={onBlur}
      placeholder={placeholder}
      autoComplete={autoComplete}
      maxLength={maxLength}
      inputMode={inputMode}
      aria-invalid={Boolean(error)}
      aria-describedby={error ? `reg-${name}-error` : hint ? `reg-${name}-hint` : undefined}
      className={`${fieldClass} ${error ? fieldErrorClass : ''}`}
    />
    {hint && !error ? (
      <div id={`reg-${name}-hint`} className='mt-1 pl-1 text-[11px] text-white/45'>
        {hint}
      </div>
    ) : null}
    <FieldError id={`reg-${name}-error`} error={error} />
  </div>
)

// Native <select>, so typing "CO" on the keyboard jumps straight to Colorado.
export const StateSelect = ({ value, onChange, onBlur, error }) => (
  <div>
    <label htmlFor='reg-shop_state' className={labelClass}>
      State<span className='text-white/40'> *</span>
    </label>
    <div className='relative'>
      <select
        id='reg-shop_state'
        name='shop_state'
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        autoComplete='address-level1'
        aria-invalid={Boolean(error)}
        aria-describedby={error ? 'reg-shop_state-error' : undefined}
        className={`${fieldClass} cursor-pointer appearance-none pr-9 ${value ? '' : 'text-white/45'} ${error ? fieldErrorClass : ''}`}
      >
        <option value='' className='bg-[#0F2035] text-white/60'>
          Select
        </option>
        {US_STATES.map((s) => (
          <option key={s.code} value={s.code} className='bg-[#0F2035] text-white'>
            {s.code} — {s.name}
          </option>
        ))}
      </select>
      <ChevronDown className='pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-white/50' aria-hidden='true' />
    </div>
    <FieldError id='reg-shop_state-error' error={error} />
  </div>
)

// Which fields each sign-up step owns, so a step checks only its own fields before moving on.
export const SHOP_FIELDS = ['company_name', 'email', 'phone', 'shop_address', 'shop_city', 'shop_state', 'shop_zip']
export const ACCOUNT_FIELDS = ['owner_name', 'owner_email', 'owner_phone', 'password', 'confirm']

const pick = (errors, fields) => Object.fromEntries(Object.entries(errors).filter(([k]) => fields.includes(k)))

/** The shop step's errors only. */
export const validateShop = (f) => pick(validateRegistration(f), SHOP_FIELDS)

/** The account step's errors only. */
export const validateAccount = (f) => pick(validateRegistration(f), ACCOUNT_FIELDS)

/** The form as the API takes it: trimmed, the state upper-case and the emails lower-case; no confirm field. */
export const toRegistrationPayload = (form) => {
  const { confirm, password, ...fields } = form
  const payload = Object.fromEntries(Object.entries(fields).map(([k, v]) => [k, String(v ?? '').trim()]))
  return {
    ...payload,
    password,
    shop_state: payload.shop_state.toUpperCase(),
    owner_email: payload.owner_email.toLowerCase(),
    email: payload.email.toLowerCase()
  }
}

/**
 * The value to store when a sign-up field changes. Phone fields keep just the digits (up to 10) and show them as
 * (303) 555-0142; a pasted "+1 303-555-0142" is cleaned up, and backspacing over a ")" / " " / "-" removes the digit
 * before it, as a phone field normally behaves.
 */
export const nextFieldValue = (name, typed, stored) => {
  if (!PHONE_FIELDS.includes(name)) return typed
  const digits = usPhoneDigits(typed)
  const shown = formatUsPhone(stored)
  // One character gone but the digits unchanged → a ")", " " or "-" was deleted: delete the digit just before it.
  if (typed.length === shown.length - 1 && digits === stored) {
    let at = 0
    while (at < typed.length && typed[at] === shown[at]) at += 1
    const digitsBefore = shown.slice(0, at).replace(/\D/g, '').length
    return digits.slice(0, Math.max(0, digitsBefore - 1)) + digits.slice(digitsBefore)
  }
  return digits
}
